import { useState, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import BudgetUnitSelect, { budgetUnitLabel } from '../../Common/BudgetUnitSelect';
import MoneyDisplay from '../../Common/MoneyDisplay';
import DebouncedNumberInput from '../../Common/DebouncedNumberInput';
import DebouncedInput from '../../Common/DebouncedInput';
import IntentsTable from '../../Common/IntentsTable';
import IntentDrawer from '../../Common/IntentDrawer';
import { PlusOutlined, DeleteOutlined, EditFilled, UpOutlined, DownOutlined, WarningOutlined, EditOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  InputNumber,
  Space,
  Table,
  Row,
  Tooltip,
} from 'antd';

const REQUIRED_ROW_FIELDS = { title: 'Naziv', price: 'Cena' };

const missingRowFields = (row) => {
  const missing = [];
  if (!row.title || String(row.title).trim() === '') missing.push('title');
  if (row.price === null || row.price === undefined || row.price === '') missing.push('price');
  return missing;
};

// -> { errors: { 'sectionInx-rowInx': ['title', ...] },
//      problems: [{ sectionInx, category, rows: [1-based row numbers] }] }
const collectRowErrors = (data) => {
  const errors = {};
  const problems = [];
  data.forEach((section, sectionInx) => {
    const rows = [];
    section.rows?.forEach((row, rowInx) => {
      const missing = missingRowFields(row);
      if (missing.length === 0) return;
      errors[`${sectionInx}-${rowInx}`] = missing;
      rows.push(rowInx + 1);
    });
    if (rows.length > 0) {
      problems.push({
        sectionInx,
        category: section.category || 'Neimenovana kategorija',
        rows,
      });
    }
  });
  return { errors, problems };
};

const missingLabels = (fields) => fields.map(f => REQUIRED_ROW_FIELDS[f]).join(' in ');

const MissingHint = ({ children }) => (
  <span style={{ color: '#ff4d4f', fontStyle: 'italic' }}>{children}</span>
);


const BudgetExpensesByCategoryView = ({expensesData, setExpensesData, editingSection, setEditingSection, onDataRefresh, onHasIntentsChange, onValidationChange, onRowValidationChange, readOnly = false}) => {

  const [editedIntentId, setEditedIntentId] = useState(null);
  const [intentDrawerOpen, setIntentDrawerOpen] = useState(false);
  // Errors are derived from the data rather than stored: a stored map keyed by
  // "sectionInx-rowInx" pointed at the wrong rows as soon as one was deleted or
  // moved. `showErrors` only decides whether we paint them -- it flips on the
  // first time the user closes a section or saves, and off once all rows pass.
  const [showErrors, setShowErrors] = useState(false);
  const sectionRefs = useRef({});

  const { errors: incompleteRows, problems } = useMemo(
    () => collectRowErrors(expensesData),
    [expensesData]
  );

  const getRowErrors = (sectionInx, rowInx) =>
    (showErrors && incompleteRows[`${sectionInx}-${rowInx}`]) || [];

  const getFieldError = (sectionInx, rowInx, field) =>
    getRowErrors(sectionInx, rowInx).includes(field);

  const getSectionProblem = (sectionInx) =>
    showErrors ? problems.find(p => p.sectionInx === sectionInx) : undefined;

  // Marks every incomplete row (the marks stay until the fields are filled in)
  // and reports what is missing so the caller can name it in its own message.
  // `sectionInx` scopes the verdict to one section without hiding the others.
  const validateRows = ({ scrollToFirst = false, sectionInx = null } = {}) => {
    const scoped = sectionInx === null
      ? problems
      : problems.filter(p => p.sectionInx === sectionInx);
    setShowErrors(problems.length > 0);

    if (scrollToFirst && problems.length > 0) {
      const first = problems[0];
      // Only take over the edit focus when no section is open -- switching
      // sections unmounts the inputs and drops keystrokes not yet debounced.
      if (editingSection === null) setEditingSection(first.sectionInx);
      requestAnimationFrame(() =>
        sectionRefs.current[first.sectionInx]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      );
    }
    return { valid: scoped.length === 0, problems: scoped };
  };

  // Notify parent about validation state
  useEffect(() => {
    if (onRowValidationChange) {
      onRowValidationChange(() => validateRows({ scrollToFirst: true }));
    }
  }, [expensesData, editingSection]);

  // Check if any row in a section has intents
  const sectionHasIntents = (section) => {
    return section.rows?.some(row => row.intents && row.intents.length > 0);
  };

  // Check if any section has intents (for parent component)
  useEffect(() => {
    if (onHasIntentsChange) {
      const hasAnyIntents = expensesData.some(section => sectionHasIntents(section));
      onHasIntentsChange(hasAnyIntents);
    }
  }, [expensesData, onHasIntentsChange]);

  // Check if all categories have dates (for validation)
  useEffect(() => {
    if (onValidationChange) {
      const categoriesWithoutDate = expensesData
        .filter(section => !section.date)
        .map(section => section.category || 'Neimenovana kategorija');
      onValidationChange(categoriesWithoutDate);
    }
  }, [expensesData, onValidationChange]);

  const BASE_ROW_DATA = {
      id: null,
      title: '',
      quantity: 1,
      unit: 'day',
      n_units: 1,
      price: null,
      discount: 0,
      intent: null
  }
  const BASE_CAT_DATA = {
    category: '',
    date: null,
    rows: [BASE_ROW_DATA]
  }


  const onAddNewSection = () => {
    setExpensesData(prev => {
      const next = [...prev, { ...BASE_CAT_DATA, rows: [{ ...BASE_ROW_DATA, id: null }] }];
      setEditingSection(next.length - 1);
      return next;
    });
  }
  const onDeleteSection = (sectionInx) => {
    setExpensesData(prev => prev.filter((_, idx) => idx !== sectionInx));
    setEditingSection(prev =>
      prev === sectionInx ? null : prev > sectionInx ? prev - 1 : prev
    );
  };
  const onDeleteRow = (sectionInx, rowInx) => {
    const updatedRowsData = [...expensesData];
    updatedRowsData[sectionInx].rows.splice(rowInx, 1);
    setExpensesData(updatedRowsData);
  }
  const onAddNewRow = (sectionInx) => {
    const updatedRowsData = [...expensesData];
    updatedRowsData[sectionInx].rows.push(BASE_ROW_DATA);
    setExpensesData(updatedRowsData);
  }

  const onMoveRow = (sectionInx, rowInx, direction) => {
    const newInx = rowInx + direction;
    setExpensesData(prev => {
      const updated = [...prev];
      const rows = [...updated[sectionInx].rows];
      if (newInx < 0 || newInx >= rows.length) return prev;
      [rows[rowInx], rows[newInx]] = [rows[newInx], rows[rowInx]];
      updated[sectionInx] = { ...updated[sectionInx], rows };
      return updated;
    });
  }

  const onMoveSection = (sectionInx, direction) => {
    const newInx = sectionInx + direction;
    if (newInx < 0 || newInx >= expensesData.length) return;
    setExpensesData(prev => {
      const updated = [...prev];
      [updated[sectionInx], updated[newInx]] = [updated[newInx], updated[sectionInx]];
      return updated;
    });
    // Update editingSection if it was one of the moved sections
    if (editingSection === sectionInx) {
      setEditingSection(newInx);
    } else if (editingSection === newInx) {
      setEditingSection(sectionInx);
    }
  }

  const onRowValueChange = (sectionInx, rowInx, key, value) => {
    setExpensesData(prev =>
      prev.map((section, sIdx) =>
        sIdx !== sectionInx
          ? section
          : {
              ...section,
              rows: section.rows.map((row, rIdx) =>
                rIdx !== rowInx
                  ? row
                  : { ...row, [key]: value }
              ),
              ...(rowInx === null ? { [key]: value } : {})
            }
      )
    );
  };

  const getSectionTotals = (section) => {
    const total = section.rows.reduce((acc, row) => {
      const sum =
        (row.price || 0) *
        (row.quantity || 0) *
        (row.n_units || 0);
      return acc + sum;
    }, 0);

    const finalTotal = section.rows.reduce((acc, row) => {
      const sum =
        (row.price || 0) *
        (row.quantity || 0) *
        (row.n_units || 0);
      const final = sum - (sum * (row.discount || 0)) / 100;
      return acc + final;
    }, 0);

    return { total, finalTotal };
  };

    const getColumns = (sectionInx, isEditing) => {
      let cols = [
        {
          title: '',
          dataIndex: 'out_of_sync',
          width: 40,
          render: (outOfSync, row, rowInx) => (
            <Space size={4}>
              {getRowErrors(sectionInx, rowInx).length > 0 && (
                <Tooltip title={`Manjka ${missingLabels(getRowErrors(sectionInx, rowInx))}.`}>
                  <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />
                </Tooltip>
              )}
              {outOfSync && (
                <Tooltip title="Zneski transakcij se ne ujemajo s proračunom zaradi ročnih sprememb na transakcijah.">
                  <WarningOutlined style={{ color: '#faad14' }} />
                </Tooltip>
              )}
              {row.has_manual_override && (
                <Tooltip title="Ena ali več transakcij ima ročno spremenjene vrednosti — te ne bodo posodobljene ob shranjevanju nove verzije.">
                  <EditOutlined style={{ color: '#1677ff' }} />
                </Tooltip>
              )}
            </Space>
          ),
        },
        {
          title: 'Naziv',
          dataIndex: 'title',
          render: (title, row, rowInx) =>
            isEditing ? (
              <DebouncedInput
                size="small"
                value={title}
                status={getFieldError(sectionInx, rowInx, 'title') ? 'error' : undefined}
                onChange={val => onRowValueChange(sectionInx, rowInx, 'title', val)}
              />
            ) : getFieldError(sectionInx, rowInx, 'title') ? (
              <MissingHint>Manjka naziv</MissingHint>
            ) : <b>{title}</b>
        },
        {
          title: 'Količina',
          dataIndex: 'quantity',
          render: (quant, row, rowInx) =>
            isEditing ? (
              <InputNumber
                size="small"
                min={0}
                value={quant}
                onChange={val =>
                  onRowValueChange(sectionInx, rowInx, 'quantity', val)
                }
              />
            ) : (
              quant
            ),
        },
        {
          title: 'Enota',
          dataIndex: 'unit',
          render: (unit, row, rowInx) =>
            isEditing ? (
              <BudgetUnitSelect
                size="small"
                value={unit}
                onChange={val =>
                  onRowValueChange(sectionInx, rowInx, 'unit', val)
                }
              />
            ) : (
              budgetUnitLabel(unit)
            ),
        },
        {
          title: 'Št. enot',
          dataIndex: 'n_units',
          render: (n_units, row, rowInx) =>
            isEditing ? (
              <InputNumber
                size="small"
                min={0}
                value={n_units}
                onChange={val =>
                  onRowValueChange(sectionInx, rowInx, 'n_units', val)
                }
              />
            ) : (
              n_units
            ),
        },
        {
          title: 'Cena',
          dataIndex: 'price',
          render: (price, row, rowInx) =>
            isEditing ? (
              <DebouncedNumberInput
                size="small"
                value={price}
                placeholder="Vnesi znesek"
                status={getFieldError(sectionInx, rowInx, 'price') ? 'error' : undefined}
                onChange={val => onRowValueChange(sectionInx, rowInx, 'price', val)}
              />
            ) : getFieldError(sectionInx, rowInx, 'price') ? (
              <MissingHint>Manjka cena</MissingHint>
            ) : (
              <MoneyDisplay size="small" type="EXPENSE" val={price} />
            ),
        },
        {title: 'Vsota', dataIndex: null, render: (x, row) => {
          const expected = row.price * row.quantity * row.n_units * (1 - (row.discount || 0) / 100);
          const intentTotal = (row.intents || []).reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
          const mismatch = row.intents?.length > 0 && Math.abs(intentTotal - expected) > 0.01;
          const diff = intentTotal - expected;
          const diffPct = expected !== 0 ? (diff / expected) * 100 : 0;
          return (
            <span>
              <MoneyDisplay size='small' type='EXPENSE' val={row.price * row.quantity * row.n_units} />
              {mismatch && (
                <Tooltip title={`Skupni znesek transakcij (${intentTotal.toFixed(2)}) se ne ujema s proračunom zaradi ročnih sprememb.`}>
                  <span style={{ color: '#faad14', marginLeft: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
                    <WarningOutlined style={{ marginRight: 3 }} />
                    {diff > 0 ? '+' : ''}{diff.toFixed(2)} ({diff > 0 ? '+' : ''}{diffPct.toFixed(1)}%)
                  </span>
                </Tooltip>
              )}
            </span>
          );
        }},
        {
          title: 'Popust %',
          dataIndex: 'discount',
          render: (discount, row, rowInx) =>
            isEditing ? (
              <InputNumber
                size="small"
                addonAfter="%"
                min={0}
                max={100}
                value={discount}
                onChange={val =>
                  onRowValueChange(sectionInx, rowInx, 'discount', val)
                }
              />
            ) : (
              `${discount || 0}%`
            ),
        },
        {title: 'Končno', dataIndex: null, render: (x, row, rowInx) => {
          const sum = row.price * row.quantity * row.n_units
          const final = sum - sum*row.discount/100
          return <MoneyDisplay size='small' type='EXPENSE' val={final} />}
        },
      ]
      if (editingSection == sectionInx){
        cols.push(
          {
            title: '',
            dataIndex: null,
            width: 120,
            render: (_, row, rowInx) =>
              isEditing ? (
                <Space size="small">
                  <Button
                    size="small"
                    disabled={rowInx === 0}
                    onClick={() => onMoveRow(sectionInx, rowInx, -1)}
                  >
                    <UpOutlined />
                  </Button>
                  <Button
                    size="small"
                    disabled={rowInx === expensesData[sectionInx].rows.length - 1}
                    onClick={() => onMoveRow(sectionInx, rowInx, 1)}
                  >
                    <DownOutlined />
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => onDeleteRow(sectionInx, rowInx)}
                  >
                    <DeleteOutlined />
                  </Button>
                </Space>
              ) : null,
          }
        )
      }
      return cols
    }


  return (
    <>
      <IntentDrawer
        open={intentDrawerOpen}
        onCancel={() => {
          setIntentDrawerOpen(false);
          setEditedIntentId(null);
        }}
        onDone={() => {
          if (onDataRefresh) onDataRefresh();
          setEditedIntentId(null);
          setIntentDrawerOpen(false);
        }}
        onDelete={() => {
          if (onDataRefresh) onDataRefresh();
          setEditedIntentId(null);
          setIntentDrawerOpen(false);
        }}
        editedIntentId={editedIntentId}
      />

      {expensesData.map((sectionData, sectionInx) => {
        const sectionProblem = getSectionProblem(sectionInx);
        return (
          <div
            key={`expenses-data-sect-${sectionInx}`}
            ref={el => { sectionRefs.current[sectionInx] = el; }}
            style={{ marginBottom: '50px' }}
          > {/* Added spacing here */}
            <Row
              align="middle"
              justify="space-between"
              style={{ marginBottom: 8 }}
            >
              <Col>
                <Space align="center">
                  {editingSection === sectionInx ? (
                    <DebouncedInput
                      placeholder="Kategorija"
                      size="small"
                      value={sectionData.category}
                      onChange={val =>
                        onRowValueChange(sectionInx, null, 'category', val)
                      }
                    />
                  ) : (
                    <h2 style={{ margin: 0 }}>
                      {sectionData.category || 'Neimenovana kategorija'}
                    </h2>
                  )}

                  {sectionProblem && (
                    <Tooltip
                      title={`Nepopolne vrstice (manjka Naziv ali Cena): ${sectionProblem.rows.join(', ')}`}
                    >
                      <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />
                    </Tooltip>
                  )}

                  {(
                    <Button
                      size="small"
                      type={editingSection === sectionInx ? 'primary' : 'default'}
                      onClick={() => {
                        if (editingSection === sectionInx) {
                          // Clicking Done - only this section's rows may keep
                          // it open, but incomplete rows elsewhere get marked
                          // too so they are not left invisible.
                          if (validateRows({ sectionInx }).valid) {
                            setEditingSection(null);
                          }
                        } else {
                          // Clicking Edit
                          setEditingSection(sectionInx);
                        }
                      }}
                    >
                      {editingSection === sectionInx ? 'Končano' : <EditFilled />}
                    </Button>
                  )}

                  {editingSection === sectionInx && (
                    <>
                      <Button
                        size="small"
                        disabled={sectionInx === 0}
                        onClick={() => onMoveSection(sectionInx, -1)}
                      >
                        <UpOutlined />
                      </Button>
                      <Button
                        size="small"
                        disabled={sectionInx === expensesData.length - 1}
                        onClick={() => onMoveSection(sectionInx, 1)}
                      >
                        <DownOutlined />
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() => onDeleteSection(sectionInx)}
                      >
                        <DeleteOutlined /><span>Izbriši celoten odsek</span>
                      </Button>
                    </>
                  )}
                </Space>
              </Col>

              <Col>
                <Space>
                  <span>Datum namenov</span>
                  <DatePicker
                    size="small"
                    style={{ width: 160 }}
                    disabled={editingSection !== sectionInx}
                    value={sectionData.date ? dayjs(sectionData.date) : null}
                    onChange={(_date, dateString) =>
                      onRowValueChange(sectionInx, null, 'date', dateString || null)
                    }
                  />
                </Space>
              </Col>
            </Row>
            <Table
              className="proj-budget-table"
              size="small"
              columns={getColumns(sectionInx, editingSection === sectionInx)}
              dataSource={sectionData.rows}
              pagination={false}
              rowKey={(row, index) => row.id ?? `new-${index}`}
              rowClassName={(row, rowInx) =>
                getRowErrors(sectionInx, rowInx).length > 0 ? 'budget-row-error' : ''
              }
              expandable={{
                expandedRowRender: (row) => (
                  <IntentsTable
                    intentsData={{ results: row.intents || [] }}
                    onIntentEdit={(intent) => {
                      setEditedIntentId(intent.id);
                      setIntentDrawerOpen(true);
                    }}
                    onIntentChanged={onDataRefresh}
                    setSelectedIds={() => {}}
                  />
                ),
                rowExpandable: (row) => row.intents && row.intents.length > 0,
              }}
            />
            {(() => {
              const { total, finalTotal } = getSectionTotals(sectionData);
              const intentSectionTotal = sectionData.rows.reduce((acc, row) => {
                return acc + (row.intents || []).reduce((s, i) => s + parseFloat(i.amount || 0), 0);
              }, 0);
              const sectionHasAnyIntents = sectionData.rows.some(row => row.intents?.length > 0);
              const sectionMismatch = sectionHasAnyIntents && Math.abs(intentSectionTotal - finalTotal) > 0.01;
              const sectionDiff = intentSectionTotal - finalTotal;
              const sectionDiffPct = finalTotal !== 0 ? (sectionDiff / finalTotal) * 100 : 0;

              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 32,
                    marginTop: 8,
                    paddingRight: 8,
                  }}
                >
                  <div>
                    <strong>Skupaj:</strong>{' '}
                    <MoneyDisplay size="small" type="EXPENSE" val={total} />
                  </div>

                  <div>
                    <strong>Končno skupaj:</strong>{' '}
                    <MoneyDisplay size="small" type="EXPENSE" val={finalTotal} />
                    {sectionMismatch && (
                      <Tooltip title={`Skupni znesek transakcij (${intentSectionTotal.toFixed(2)}) se ne ujema s proračunom kategorije.`}>
                        <span style={{ color: '#faad14', marginLeft: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
                          <WarningOutlined style={{ marginRight: 3 }} />
                          {sectionDiff > 0 ? '+' : ''}{sectionDiff.toFixed(2)} ({sectionDiff > 0 ? '+' : ''}{sectionDiffPct.toFixed(1)}%)
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })()}
            {editingSection === sectionInx && (
              <Button onClick={() => onAddNewRow(sectionInx)}>
                <span>Dodaj novo vrstico</span>
                <PlusOutlined />
              </Button>
            )}
            <hr/>
          </div>
        )
      })}
      <Button onClick={onAddNewSection}><span>Dodaj nov odsek</span><PlusOutlined /></Button>
    </>
  );
};

export default BudgetExpensesByCategoryView;
